import React, { useCallback, useEffect, useRef, useState } from "react";
import PatientHistoryTimelineRow from "./PatientHistoryTimelineRow";
import {
  GetHistoryDto,
  FindAllHistoryQueryFiltersDto,
  HistoryFiltersDto,
  GetHistoryDtoTypeEnum,
} from "../../../api-client";
import useStore from "../../../helpers/useStore";
import { HistoryContext } from "../../../stores/History/history.provider";
import { CircularProgress } from "@mui/material";
import { UserContext } from "../../../stores/User/User.provider";

interface PatientHistoryTimelineProps {
  patientId: number;
}
export default function PatientHistoryTimeline({ patientId }: PatientHistoryTimelineProps) {
  const UserStore = useStore(UserContext);
  const { user, getRoles, listPermissions, apiInfo, permissionMap } = UserStore;
  const HistoryStore = useStore(HistoryContext);
  const { getAllHistory } = HistoryStore;
  const [loading, setLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [histories, setHistories] = useState<GetHistoryDto[]>([]);
  // patientId is deliberately NOT part of this state: it is a prop, and copying
  // it in at mount is what made the timeline keep the previous patient after a
  // patient tab switch (MD-475). It is passed to getAllHistory at call time.
  const [fetchedApiFilters, setFetchedApiFilters] = useState<FindAllHistoryQueryFiltersDto>({
    limit: 10,
    page: 1,
    action: null,
    // type: GetHistoryDtoTypeEnum.Patient,
    type: undefined,
    fromDate: null,
    toDate: null,
    searchKey: null,
  });

  // The component is rendered without a key, so switching patient tabs only
  // changes the prop on a live instance. Reset the list and paging here, during
  // render, rather than in an effect: an effect-based reset would let the fetch
  // effect below run once with the previous patient's page number before the
  // reset lands, firing two requests for one switch.
  const [renderedPatientId, setRenderedPatientId] = useState<number>(patientId);
  if (renderedPatientId !== patientId) {
    setRenderedPatientId(patientId);
    setHistories([]);
    setHasMore(false);
    setLoading(true);
    setFetchedApiFilters((prevFilters) =>
      prevFilters.page === 1 ? prevFilters : { ...prevFilters, page: 1 }
    );
  }

  // Every fetch takes the next sequence number; a response that is no longer
  // the latest request is dropped. Without this, switching A -> B -> A quickly
  // lets an in-flight request for the earlier patient land in the new list.
  const requestSeq = useRef(0);

  // Define the type of observer.current
  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setFetchedApiFilters((prevFilters) => ({
            ...prevFilters,
            page: prevFilters.page! + 1,
          }));
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );
  useEffect(() => {
    if (patientId && user?.id) {
      fetchData();
    }
  }, [fetchedApiFilters, patientId, user?.id]);
  const fetchData = async () => {
    const requestId = ++requestSeq.current;
    const isCurrentRequest = () => requestId === requestSeq.current;
    try {
      setLoading(true);

      const response = await getAllHistory({ ...fetchedApiFilters, patientId });
      if (!isCurrentRequest()) return;
      if (response?.length) {
        setHistories((prevHistories) => [...prevHistories, ...response]);
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      if (!isCurrentRequest()) return;
      console.error("Error fetching history data:", error);
    } finally {
      if (isCurrentRequest()) {
        setLoading(false);
      }
    }
  };

  return (
    <div>
      {histories.map((history, index) => (
        <div ref={histories?.length == index + 1 ? lastElementRef : null}>
          <PatientHistoryTimelineRow key={history.id} history={history} />
        </div>
      ))}
      {loading && (
        <div>
          <CircularProgress size={60} />
          Loading...
        </div>
      )}
    </div>
  );
}
